import { useState } from 'react'
import {
  App, Button, Card, Col, Drawer, Empty, Row, Space, Statistic, Table, Tag,
  Typography,
} from 'antd'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { maintenanceKpiApi } from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { MachineHealthInfo } from '@/shared/types/maintenance'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'

const { Text } = Typography

/**
 * Spec §15 — Machine Health / History.
 * Manager-facing list (one row per active machine) + click-through detail
 * drawer with last 100 work orders + last/next PM + spare consumption.
 */
export default function MachineHealthPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [openMachineId, setOpenMachineId] = useState<string | null>(null)

  const { data: machinesData, isLoading } = useQuery({
    queryKey: ['machines-for-health', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 200 }),
  })
  const machines = machinesData?.items ?? []

  // Pull health summary per machine. We do this in parallel so we can keep
  // the list snappy. The history endpoint already exposes the score.
  const healthQueries = useQueries({
    queries: machines.map((m) => ({
      queryKey: ['machine-health', m.id],
      enabled: !!m.id,
      queryFn: () => maintenanceKpiApi.machineHistory(m.id, 1),
      staleTime: 60_000,
    })),
  })
  const healthByMachine: Record<string, { health_score: number; health_status: string }> = {}
  machines.forEach((m, i) => {
    const d = healthQueries[i]?.data
    if (d) healthByMachine[m.id] = { health_score: d.health_score, health_status: d.health_status }
  })

  // Detail drawer history
  const { data: history } = useQuery({
    queryKey: ['machine-history-detail', openMachineId],
    enabled: !!openMachineId,
    queryFn: () => maintenanceKpiApi.machineHistory(openMachineId!, 100),
  })

  const recomputeMutation = useMutation({
    mutationFn: (id: string) => maintenanceKpiApi.recomputeHealth(id),
    onSuccess: (h: MachineHealthInfo) => {
      message.success(`Health re-scored: ${h.health_score} (${h.health_status})`)
      qc.invalidateQueries({ queryKey: ['machine-health'] })
      qc.invalidateQueries({ queryKey: ['machine-history-detail'] })
    },
  })

  const machineColumns = [
    { title: 'Code', dataIndex: 'machine_id_str', key: 'code', width: 120 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Section', dataIndex: 'section', key: 'section', width: 140 },
    {
      title: 'Health score', key: 'score', width: 130,
      render: (_: unknown, m: typeof machines[number]) => {
        const h = healthByMachine[m.id]
        if (!h) return <Text type="secondary">—</Text>
        const color = h.health_score >= 80 ? '#52c41a' : h.health_score >= 60 ? '#faad14' : '#cf1322'
        return <Text strong style={{ color }}>{h.health_score}</Text>
      },
    },
    {
      title: 'Status', key: 'status', width: 150,
      render: (_: unknown, m: typeof machines[number]) =>
        <MaintenanceStatusBadge status={healthByMachine[m.id]?.health_status} />,
    },
    {
      title: 'Criticality', dataIndex: 'criticality_rating', key: 'cr', width: 110,
      render: (v: string) => <Tag color={v === 'CRITICAL' ? 'red' : v === 'HIGH' ? 'orange' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_: unknown, m: typeof machines[number]) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => setOpenMachineId(m.id)}>History</Button>
          <Button size="small" type="link" onClick={() => recomputeMutation.mutate(m.id)} loading={recomputeMutation.isPending}>
            Re-score
          </Button>
        </Space>
      ),
    },
  ]

  const woColumns = [
    { title: 'WO', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    { title: 'Type', dataIndex: 'maintenance_type', key: 'mt', width: 110 },
    { title: 'Priority', dataIndex: 'priority', key: 'pri', width: 90 },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 130 },
    {
      title: 'Reported', dataIndex: 'reported_datetime', key: 'rep', width: 130,
      render: (v: string | undefined) => v ? dayjs(v).format('DD MMM HH:mm') : '—',
    },
    { title: 'Downtime', dataIndex: 'downtime_minutes', key: 'dt', width: 100,
      render: (v: number | undefined) => v != null ? `${v} min` : '—' },
  ]

  const pmColumns = [
    { title: 'Schedule', dataIndex: 'schedule_no', key: 'sn', width: 130 },
    { title: 'Frequency', dataIndex: 'frequency', key: 'fr', width: 110 },
    {
      title: 'Planned date', dataIndex: 'planned_date', key: 'pd', width: 130,
      render: (v: string | undefined) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 110 },
  ]

  return (
    <div>
      <PageHeader
        title="Machine Health"
        subtitle="Live health score, status band, and full maintenance history."
        extra={<Button icon={<ReloadOutlined />} onClick={() => qc.invalidateQueries({ queryKey: ['machine-health'] })}>Refresh</Button>}
      />

      <Table
        rowKey="id"
        columns={machineColumns}
        dataSource={machines}
        loading={isLoading}
        size="middle"
        pagination={false}
      />

      <Drawer
        title={`Machine history`}
        open={!!openMachineId}
        onClose={() => setOpenMachineId(null)}
        width={780}
        destroyOnClose
      >
        {history ? (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Health score" value={history.health_score} /></Col>
              <Col span={8}><Statistic title="Status" valueRender={() => <MaintenanceStatusBadge status={history.health_status} />} /></Col>
              <Col span={8}><Statistic title="Open WOs" value={history.open_work_orders} /></Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Open breakdowns" value={history.open_breakdowns} /></Col>
              <Col span={8}><Statistic title="Repeat-30d" value={history.repeat_breakdown_count} /></Col>
              <Col span={8}><Statistic title="Last PM"
                value={history.last_pm_at ? dayjs(history.last_pm_at).format('DD MMM') : '—'} /></Col>
            </Row>
            <Card size="small" title="Recent work orders" style={{ marginBottom: 12 }}>
              {history.work_orders.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No work orders" />
                : <Table rowKey="work_order_no" size="small" pagination={false}
                    columns={woColumns}
                    dataSource={history.work_orders as unknown as Array<Record<string, unknown>>} />}
            </Card>
            <Card size="small" title="PM schedules">
              {history.pm_schedules.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No PM schedules" />
                : <Table rowKey="schedule_no" size="small" pagination={false}
                    columns={pmColumns}
                    dataSource={history.pm_schedules as unknown as Array<Record<string, unknown>>} />}
            </Card>
          </>
        ) : <Empty description="Loading…" />}
      </Drawer>
    </div>
  )
}
